import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { pathOr } from 'ramda';
import { replace } from 'react-router-redux';
import AirportDelayMapZoomControl from './children/AirportDelayMapZoomControl';
import componentBase from '../../../src/lib/component-base';
import { actions, selectors } from '../redux';
import * as delayIndex from '../../../shared/lib/flex-delay-index';
import Ad from '../../../shared/components/Ad/Ad';
import BannerAd from '../../../shared/components/BannerAd/BannerAd';
import { AirportDelayMap as airportDelayMapAds } from './ads';
import { addMetaCanonical } from '../../../shared/lib/meta-tags';
import Sidebar from './children/AirportDelayMap/children/Sidebar';
import { getRegion } from './regions';

let AirportDelayMap = null;

const {
  endSpinner,
  loadDelayIndices,
  loadDetailsByCode,
  setRegion,
  setAirport,
  startSpinner,
  updateMap,
  updateRegion,
} = actions;

const {
  getTrackerState,
} = selectors;

@connect((state) => {
  const airportDelayMap = state.AirportDelayMap;
  const delayIndices = airportDelayMap.delayIndices;
  const delayObj = airportDelayMap.delayObj;
  const defaultAirports = airportDelayMap.defaultAirports;
  const selectedState = getTrackerState(state);

  return {
    delayIndices,
    defaultAirports,
    delayObj,
    airport: airportDelayMap.airport,
    center: airportDelayMap.center,
    error: airportDelayMap.error,
    loading: airportDelayMap.loading,
    region: airportDelayMap.region,
    sidebarState: selectedState,
    zoom: airportDelayMap.zoom,
  };
})
@componentBase('DelayMap')
export default class DelayMap extends React.Component {
  static propTypes = {
    airport: PropTypes.string,
    center: PropTypes.array,
    defaultAirports: PropTypes.array,
    delayIndices: PropTypes.array,
    delayObj: PropTypes.object,
    dispatch: PropTypes.func,
    loading: PropTypes.bool,
    location: PropTypes.object,
    match: PropTypes.object,
    name: PropTypes.string,
    region: PropTypes.string,
    sidebarState: PropTypes.object,
    zoom: PropTypes.number,
  }

  constructor(props, context) {
    super(props, context);

    this.updateAirport = this.updateAirport.bind(this);
  }

  componentDidMount() {
    if (!AirportDelayMap) {
      /*
        AirportDelayMap should not be `imported`
        AirportDelayMap child components
          Map
          AirportDelays
          SatTileLayer
          MapLabelsLayer
          MapRadarLayer
          MapZoomControl

          by way of leaflet, use `window` methods
          and do not appreciate server side rendering
      */
      AirportDelayMap = require('./children/AirportDelayMap/AirportDelayMap').default; //eslint-disable-line
    }

    if (!this.props.airport) {
      this.props.dispatch(startSpinner());
    }

    this.setRegionFromParam(this.props);

    if (!this.props.loading) {
      this.props.dispatch(loadDelayIndices());
    }

    this.forceUpdate();
  }

  componentWillReceiveProps(nextProps) {
    this.setRegionFromParam(nextProps);

    if ((!this.props.delayObj && nextProps.delayObj) || this.props.region !== nextProps.region) {
      this.loadRandomAirport(nextProps);

      if (this.props.loading) {
        this.props.dispatch(endSpinner());
      }
    }
  }

  componentWillUnmount() {
    if (this.props.loading) {
      this.props.dispatch(endSpinner());
    }
  }

  getMetaTags = () => {
    const title = 'Airport Delays';
    const description = 'See the current weather and delay conditions for airport worldwide, including forecast data for US airports';
    const keywords = 'airport current conditions, airport weather, airport delays';
    const metaWithoutCanonical = {
      title,
      meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: keywords },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
      ],
    };
    return addMetaCanonical(metaWithoutCanonical, this.props.location.pathname);
  }

  setAirportSession(airportCode, map) {
    if (map) { // map is only passed in when a user selects an airport on the map
      const mapCenter = map.getCenter();
      this.props.dispatch(updateMap({ center: [mapCenter.lat, mapCenter.lng], zoom: map.getZoom() }));
    }

    this.props.dispatch(setAirport(airportCode));
  }

  setRegionFromParam(props) {
    const { region: currentRegion } = this.props;
    const regionParam = pathOr('northAmerica', ['match', 'params', 'region'], props);
    const newRegion = getRegion(regionParam);
    if (newRegion !== currentRegion) {
      this.props.dispatch(setRegion(newRegion));
    }
  }

  randomAirportIndex = flightArrayLength => Math.floor(Math.random() * flightArrayLength);

  updateAirport(newAirport, map) {
    const airportCode = (newAirport && newAirport.code) ? newAirport.code : newAirport;
    this.setAirportSession(airportCode, map);
    this.props.dispatch(loadDetailsByCode({ airportCode }));
  }

  loadRandomAirport = (props) => {
    const airportCode = this.randomAirport(props);
    const { region } = props;
    this.updateAirport(airportCode);
    this.props.dispatch(updateRegion({ region, airportCode }));
  }

  randomAirport = (props) => {
    // random airport is a large/busy airport, if possible
    const { delayObj, region } = props;
    if (!delayObj) {
      // If the delayObj prop is not populated yet (first load), don't continue
      return;
    }

    let randomAirportInNewRegion = null;
    const regionArray = delayObj[region];
    const filteredRegion = delayIndex.defaultAirports(regionArray);
    const randomIndex = this.randomAirportIndex(filteredRegion.length);
    randomAirportInNewRegion = filteredRegion[randomIndex];

    return randomAirportInNewRegion.code ?
      randomAirportInNewRegion.code : randomAirportInNewRegion;
  }

  pathForRegion = region => `/airport-delays/${region}`;

  zoomToRegion = (region) => {
    const regionPath = this.pathForRegion(region);
    (regionPath !== this.props.location.pathname) && this.props.dispatch(replace(regionPath));
    // this.loadRandomAirport(this.props);
  }

  zoomToRegionHandler = region => () => this.zoomToRegion(region);

  render() {
    const meta = this.getMetaTags();

    let map = (
      <div />
    );
    if (AirportDelayMap && Sidebar) {
      const subscriptionActive = (
        this.context &&
        this.context.currentUser &&
        this.context.currentUser.subscriptionActive
      );
      const topAd = (
        <BannerAd
          topAd
          mobileAd={airportDelayMapAds.topMobile}
          desktopAd={airportDelayMapAds.top}
          subscriptionActive={subscriptionActive}
        />
      );
      const boxAd = subscriptionActive ? null : (
        <center style={{ margin: '25px 0' }}>
          <Ad
            key={airportDelayMapAds.bottom.id}
            {...airportDelayMapAds.bottom}
            subscriptionActive={subscriptionActive}
          />
        </center>
      );

      map = (
        <div>
          <div className='delay-map-container'>
            <div className='delay-map'>
              <Helmet {...meta} />
              {topAd}
              <header className='delay-map-header'>
                <h3>Airport Delays</h3>
              </header>
              <div className='map-and-sidebar'>
                <div className='delay-map-component'>
                  <AirportDelayMapZoomControl
                    zoomTo={this.zoomToRegionHandler}
                    region={this.props.region}
                  />
                  <AirportDelayMap
                    updateAirport={this.updateAirport}
                    delayIndices={this.props.delayIndices}
                    region={this.props.region}
                    center={this.props.center}
                    zoom={this.props.zoom}
                  />
                </div>
                <div className='sidebar-container'>
                  <Sidebar
                    {...this.props.sidebarState}
                    dispatch={this.props.dispatch}
                  />
                  {boxAd}
                </div>
              </div>
            </div>
            <div className='general-attribution delay-map'>
              <a id='attr-link' className='delay-map-attr' alt='link to attributions list' href='/map-attributions'>Map licensing</a>
            </div>
          </div>
        </div>
      );
    }

    return map;
  }
}



// WEBPACK FOOTER //
// ./modules/AirportDelayMap/components/container.js